import React, { useState, useRef } from 'react';
import { X, Upload, Download, FileText, AlertCircle, CheckCircle, Users, GraduationCap } from 'lucide-react';
import userService from '../services/userService';
import type { UserImportResult } from '../types';

const USER_TYPE_LABELS: Record<string, string> = {
  student: 'Học sinh',
  teacher: 'Giáo viên',
};

const USER_STATUS_LABELS: Record<string, string> = {
  active: 'Hoạt động',
  inactive: 'Không hoạt động',
  pending_verification: 'Chờ xác minh',
  suspended: 'Tạm khóa',
};

interface ImportUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

interface PreviewData {
  data: any[];
  errors: Array<{
    row: number;
    error: string;
    data: any;
  }>;
}

const ImportUserModal: React.FC<ImportUserModalProps> = ({
  isOpen,
  onClose,
  onImportSuccess
}) => {
  const [step, setStep] = useState<'upload' | 'preview' | 'import' | 'result'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importType, setImportType] = useState<'student' | 'teacher'>('student');
  const [schoolId, setSchoolId] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [importResult, setImportResult] = useState<UserImportResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError('');
    }
  };

  const handlePreview = async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    setError('');

    try {
      const result = await userService.previewImport(selectedFile, schoolId || undefined, importType);
      setPreviewData(result);
      setStep('preview');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Có lỗi xảy ra khi xem trước dữ liệu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !previewData) return;

    setIsLoading(true);
    setError('');

    try {
      const result = await userService.importUsers(
        selectedFile, 
        schoolId || undefined, 
        schoolName || undefined, 
        importType
      );
      setImportResult(result);
      setStep('result');
      if (result.success) {
        onImportSuccess();
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Có lỗi xảy ra khi import dữ liệu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadTemplate = async (type: 'student' | 'teacher') => {
    try {
      const blob = await userService.downloadTemplate(type);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_import_template.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError('Có lỗi xảy ra khi tải template');
    }
  };

  const resetModal = () => {
    setStep('upload');
    setSelectedFile(null);
    setImportType('student');
    setSchoolId('');
    setSchoolName('');
    setPreviewData(null);
    setImportResult(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Mọi đường đóng modal (X, Hủy/Đóng, Hoàn thành) đều qua đây nên reset tại đây là đủ;
  // component cha không được tự đổi isOpen = false, nếu không lần mở sau sẽ kẹt state cũ.
  const handleClose = () => {
    resetModal();
    onClose();
  };

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Chọn loại import
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => setImportType('student')}
            className={`p-4 border-2 rounded-lg text-left transition-colors ${
              importType === 'student'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <GraduationCap className="w-8 h-8 text-blue-600 mb-2" />
            <div className="font-medium">Import học sinh</div>
            <div className="text-sm text-gray-600">Import danh sách học sinh từ file CSV</div>
          </button>
          
          <button
            onClick={() => setImportType('teacher')}
            className={`p-4 border-2 rounded-lg text-left transition-colors ${
              importType === 'teacher'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Users className="w-8 h-8 text-green-600 mb-2" />
            <div className="font-medium">Import giáo viên</div>
            <div className="text-sm text-gray-600">Import danh sách giáo viên từ file CSV</div>
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Tải template
        </h3>
        <div className="flex space-x-4">
          <button
            onClick={() => handleDownloadTemplate('student')}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Tải template học sinh
          </button>
          <button
            onClick={() => handleDownloadTemplate('teacher')}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Tải template giáo viên
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Chọn file import
        </h3>
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <div className="text-lg font-medium text-gray-900 mb-2">
            {selectedFile ? selectedFile.name : 'Chọn file CSV để import'}
          </div>
          <div className="text-sm text-gray-600">
            Hỗ trợ định dạng CSV, tối đa 10MB
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          aria-label="Chọn file import"
          accept=".csv"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ID Trường học (tùy chọn)
          </label>
          <input
            type="text"
            value={schoolId}
            onChange={(e) => setSchoolId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Nhập ID trường học"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tên trường học (tùy chọn)
          </label>
          <input
            type="text"
            value={schoolName}
            onChange={(e) => setSchoolName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Nhập tên trường học"
          />
        </div>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Xem trước dữ liệu
        </h3>
        <div className="flex items-center space-x-2 text-sm">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span className="text-green-600">
            {previewData?.data.length || 0} dòng hợp lệ
          </span>
          {previewData?.errors && previewData.errors.length > 0 && (
            <>
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-red-600">
                {previewData.errors.length} dòng có lỗi
              </span>
            </>
          )}
        </div>
      </div>

      {previewData?.errors && previewData.errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-medium text-red-800 mb-2">Lỗi dữ liệu:</h4>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {previewData.errors.map((error, index) => (
              <div key={index} className="text-sm text-red-700">
                <strong>Dòng {error.row}:</strong> {error.error}
              </div>
            ))}
          </div>
        </div>
      )}

      {previewData?.data && previewData.data.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="font-medium text-gray-900">Dữ liệu hợp lệ ({previewData.data.length} dòng)</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {Object.keys(previewData.data[0] || {}).map((key) => (
                    <th key={key} className="px-4 py-2 text-left font-medium text-gray-700">
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {previewData.data.slice(0, 5).map((row, index) => (
                  <tr key={index}>
                    {Object.values(row).map((value, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-2 text-gray-900">
                        {String(value)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {previewData?.data.length && previewData.data.length > 5 && (
              <div className="px-4 py-2 bg-gray-50 text-sm text-gray-600">
                ... và {previewData.data.length - 5} dòng khác
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderResultStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        {importResult?.success ? (
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        ) : (
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
        )}
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {importResult?.success ? 'Import thành công!' : 'Import thất bại!'}
        </h3>
        <p className="text-gray-600">
          {importResult?.message}
        </p>
      </div>

      {importResult && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
              <span>Đã import: {importResult.imported?.length ?? 0} người dùng</span>
            </div>
            {importResult.errors.length > 0 && (
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                <span>Lỗi: {importResult.errors.length} dòng</span>
              </div>
            )}
          </div>
        </div>
      )}

      {importResult?.imported && importResult.imported.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="font-medium text-gray-900">
              Người dùng đã import ({importResult.imported.length})
            </h4>
          </div>
          <div className="max-h-64 overflow-auto">
            <table className="w-full text-sm" aria-label="Người dùng đã import">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Dòng</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Họ tên</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Email</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Số điện thoại</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Loại</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Trạng thái</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {importResult.imported.map(({ row, student }) => (
                  <tr key={student._id ?? row}>
                    <td className="px-4 py-2 text-gray-900 whitespace-nowrap">{row}</td>
                    <td className="px-4 py-2 text-gray-900">{student.name}</td>
                    <td className="px-4 py-2 text-gray-600">{student.email || '—'}</td>
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{student.phone || '—'}</td>
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{USER_TYPE_LABELS[student.type ?? ''] ?? '—'}</td>
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{USER_STATUS_LABELS[student.status ?? ''] ?? student.status ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {importResult?.errors && importResult.errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-medium text-red-800 mb-2">Chi tiết lỗi:</h4>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {importResult.errors.map((error, index) => (
              <div key={index} className="text-sm text-red-700">
                <strong>Dòng {error.row}:</strong> {error.error}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            Import người dùng - Bước {step === 'upload' ? '1' : step === 'preview' ? '2' : '3'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <span className="text-red-800">{error}</span>
              </div>
            </div>
          )}

          {step === 'upload' && renderUploadStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'result' && renderResultStep()}
        </div>

        <div className="flex justify-between p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {step === 'result' ? 'Đóng' : 'Hủy'}
          </button>

          <div className="flex space-x-4">
            {step === 'upload' && (
              <button
                onClick={handlePreview}
                disabled={!selectedFile || isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Đang xử lý...' : 'Xem trước'}
              </button>
            )}

            {step === 'preview' && (
              <>
                <button
                  onClick={() => setStep('upload')}
                  className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Quay lại
                </button>
                <button
                  onClick={handleImport}
                  disabled={!previewData?.data.length || isLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Đang import...' : 'Import'}
                </button>
              </>
            )}

            {step === 'result' && (
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Hoàn thành
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportUserModal;
